import React, { useCallback, useState } from 'react';
import { useCompleteRTMEnrollment } from 'hooks/useApi';
import EnrollmentStepShell from 'components/rtmEnrollment/EnrollmentStepShell';
import { getEnrollmentSteps } from 'components/rtmEnrollment/enrollmentSteps';

type RTMEnrollmentFlowProps = {
  token: string;
  patientName?: string | null;
  providerName?: string | null;
  organizationName?: string | null;
  onCompleted: () => void;
};

export default function RTMEnrollmentFlow({
  token,
  patientName,
  providerName,
  organizationName,
  onCompleted,
}: RTMEnrollmentFlowProps) {
  const [step, setStep] = useState(0);
  const {
    mutateAsync: completeEnrollmentAsync,
    isPending,
    isError,
  } = useCompleteRTMEnrollment(token);

  const enrollmentSteps = getEnrollmentSteps({ patientName, providerName, organizationName });
  const activeStep = enrollmentSteps[step];
  const totalSteps = enrollmentSteps.length;
  const isFirstStep = step === 0;
  const isLastStep = step === totalSteps - 1;
  const buttonText = isLastStep ? 'Enroll' : 'Next';

  const handleBack = useCallback(() => {
    setStep((current) => Math.max(0, current - 1));
  }, []);

  const handlePrimary = useCallback(async () => {
    if (isLastStep) {
      try {
        await completeEnrollmentAsync();
        onCompleted();
      } catch (error) {
        console.error('Error completing enrollment:', error);
      }
    } else {
      setStep((current) => current + 1);
    }
  }, [completeEnrollmentAsync, isLastStep, onCompleted]);

  return (
    <div className="flex min-h-full w-full flex-1 flex-col">
      <div className="mx-auto flex min-h-full w-full max-w-3xl flex-1 flex-col">
        <EnrollmentStepShell
          buttonText={buttonText}
          currentStep={step}
          isBackDisabled={isFirstStep}
          isPrimaryDisabled={isPending}
          onBack={handleBack}
          onPrimary={handlePrimary}
          step={activeStep}
          totalSteps={totalSteps}
        />

        {isError ? (
          <p className="mt-4 text-center text-sm text-rose-600">
            Unable to complete enrollment. Please try again later.
          </p>
        ) : null}
      </div>
    </div>
  );
}
